<template>
  <!-- Pääsuvõtmed: the logged-in person's own keys, and "Lisa see seade".
       Used on Minu seaded and on the forced "Lisa pääsuvõti" page. -->
  <div class="pk">
    <p v-if="!isSupported" class="pk-note pk-note--warn">See brauser ei toeta pääsuvõtmeid. Kasuta telefoni või uuemat brauserit.</p>

    <ul v-if="keys.length" class="pk-list">
      <li v-for="key in keys" :key="key.id" class="pk-row">
        <div class="pk-main">
          <template v-if="editing === key.id">
            <label class="pk-sr" :for="'pk-name-' + key.id">Nimi</label>
            <input :id="'pk-name-' + key.id" v-model="editName" class="pk-input" maxlength="80" @keyup.enter="saveName(key)" @keyup.esc="editing = null">
          </template>
          <b v-else>{{ key.name }} <span v-if="key.current" class="pk-tag">see seade</span></b>
          <small>Lisatud {{ day(key.created) }}<template v-if="key.used"> · viimati kasutatud {{ day(key.used) }}</template></small>
        </div>
        <div class="pk-actions">
          <template v-if="editing === key.id">
            <button type="button" class="pk-link" :disabled="busy" @click="saveName(key)">Salvesta</button>
            <button type="button" class="pk-link pk-link--quiet" @click="editing = null">Tühista</button>
          </template>
          <template v-else-if="confirming === key.id">
            <span class="pk-ask">Kustutan?</span>
            <button type="button" class="pk-link pk-link--danger" :disabled="busy" @click="removeKey(key)">Jah</button>
            <button type="button" class="pk-link pk-link--quiet" @click="confirming = null">Ei</button>
          </template>
          <template v-else>
            <button type="button" class="pk-link" @click="startEdit(key)">Muuda nime</button>
            <button type="button" class="pk-link pk-link--danger" @click="confirming = key.id">Kustuta</button>
          </template>
        </div>
      </li>
    </ul>
    <p v-else-if="loaded" class="pk-empty">Pääsuvõtmeid pole veel lisatud.</p>

    <div v-if="isSupported" class="pk-add">
      <div class="pk-field">
        <label for="pk-new-name">Selle seadme nimi</label>
        <input id="pk-new-name" v-model="newName" class="pk-input" maxlength="80">
      </div>
      <button type="button" class="pk-btn" :disabled="busy || noOwnKey" @click="addKey(false)">{{ busy === 'add' ? 'Ootan seadet…' : 'Lisa see seade' }}</button>
      <!-- On a computer: the key made on the phone instead, through the QR code
           the browser shows. A computer's own key (Windows Hello) does not
           work on the phone, and under Kohustuslik the password would not either. -->
      <button v-if="!phone" type="button" class="pk-btn pk-btn--outline" :disabled="busy" @click="addKey(true)">{{ busy === 'remote' ? 'Ootan telefoni…' : 'Lisa telefon QR-koodiga' }}</button>
    </div>
    <!-- Without this the browser asks for a USB security key, which nobody here has. -->
    <p v-if="isSupported && noOwnKey" class="pk-note pk-note--warn">Selles arvutis pole Windows Hello PIN-i seadistatud, seepärast ei saa seda arvutit lisada. Seadista see: Windowsi Sätted → Kontod → Sisselogimissuvandid → PIN (Windows Hello), siis laadi see leht uuesti. Või lisa telefon.</p>
    <!-- The QR code needs Bluetooth; without it the same button only asks for a USB key. -->
    <p v-if="isSupported && !phone && noQr" class="pk-note pk-note--warn">Selles arvutis ei saa QR-koodi näidata, sest Bluetooth puudub või on välja lülitatud. Nupp küsiks siis USB-võtit, mida pole vaja. Lülita Bluetooth sisse ja laadi leht uuesti või ava see leht telefonis ja vajuta seal „Lisa see seade”.</p>
    <p v-if="isSupported && !phone" class="pk-tip">Kasutad ka telefoni? Arvuti võti telefonis ei tööta. „Lisa telefon QR-koodiga” näitab koodi, mille skaneerid telefoniga; Bluetooth peab olema sees mõlemas seadmes. Kui telefon ütleb pärast sõrmejälge „Midagi läks valesti”, aga võti on siin nimekirjas, võib teadet eirata.</p>
    <!-- Kohustuslik or "Võtmega parool keelatud": adding the first key ends password
         login, so it is said before that happens. -->
    <p v-if="keyOnly && loaded && !keys.length" class="pk-note pk-note--warn">Kui lisad võtme, ei saa sa enam parooliga sisse. Lisa võti igale seadmele, millega sisse logid, näiteks telefonile ja arvutile.</p>
    <p v-else-if="keyOnly" class="pk-note pk-note--warn">Parool sind enam sisse ei lase. Kui logid sisse ka mõnest teisest seadmest, lisa võti ka sellele. Kui kaotad kõik võtmed, kustutab need admin.</p>
    <p v-if="error" class="pk-note pk-note--warn" role="alert">{{ error }}</p>
    <p v-if="done" class="pk-note pk-note--ok" role="status">{{ done }}</p>
  </div>
</template>

<script>
import { supported, platformAvailable, qrAvailable, mine, add, rename, remove, deviceName, day, onPhone, signalAccepted } from '@/plugins/passkey'

export default {
  emits: ['added', 'changed'],
  data () {
    return {
      keys: [],
      loaded: false,
      busy: null,
      error: '',
      done: '',
      newName: deviceName(),
      editing: null,
      editName: '',
      confirming: null,
      isSupported: supported(),
      phone: onPhone(),
      noOwnKey: false,
      noQr: false,
      keyOnly: false
    }
  },
  async mounted () {
    if (this.isSupported && !this.phone) {
      this.noOwnKey = !(await platformAvailable())
      this.noQr = !(await qrAvailable())
    }
    try {
      const data = await mine()
      this.keys = data.keys
      this.keyOnly = !!data.keyOnly
      signalAccepted(data.signal)
      // Minu seaded shows the count on the closed Pääsuvõtmed row.
      this.$emit('changed', this.keys)
    } catch (err) {
      this.error = err.message
    } finally {
      this.loaded = true
    }
  },
  methods: {
    day,
    async addKey (remote) {
      this.error = ''
      this.done = ''
      this.busy = remote ? 'remote' : 'add'
      // The name field holds this computer's name until changed; a phone's key
      // gets a phone's name instead.
      const name = remote && this.newName === deviceName() ? 'Telefon' : this.newName
      try {
        const data = await add(name, { remote })
        this.keys = data.keys
        this.done = remote
          ? 'Telefoni pääsuvõti lisatud. Kui telefon näitas „Midagi läks valesti”, võib seda eirata — võti töötab.'
          : 'Pääsuvõti lisatud. Järgmine kord logi sisse ühe puudutusega.'
        this.$emit('added', data)
        this.$emit('changed', this.keys)
      } catch (err) {
        // Cancelling is also how a person gets out of the USB key prompt.
        this.error = remote && err.cancelled
          ? 'Katkestatud. Kui QR-koodi asemel küsiti USB-võtit, pole selles arvutis Bluetooth kasutatav: lülita see sisse või ava see leht telefonis ja vajuta seal „Lisa see seade”.'
          : err.message
      } finally {
        this.busy = null
      }
    },
    startEdit (key) {
      this.confirming = null
      this.editing = key.id
      this.editName = key.name
    },
    async saveName (key) {
      this.busy = 'rename'
      this.error = ''
      try {
        this.keys = (await rename(key.id, this.editName)).keys
        this.editing = null
      } catch (err) {
        this.error = err.message
      } finally {
        this.busy = null
      }
    },
    async removeKey (key) {
      this.busy = 'remove'
      this.error = ''
      this.done = ''
      try {
        const data = await remove(key.id)
        this.keys = data.keys
        signalAccepted(data.signal)
        this.confirming = null
        this.$emit('changed', this.keys)
      } catch (err) {
        this.error = err.message
      } finally {
        this.busy = null
      }
    }
  }
}
</script>

<style scoped>
.pk { display: grid; gap: 14px; }
.pk-list { list-style: none; margin: 0; padding: 0; border: 1px solid #C9D2CE; border-radius: 8px; background: #fff; }
.pk-row { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; padding: 12px 14px; border-top: 1px solid #E1E7E4; }
.pk-row:first-child { border-top: 0; }
.pk-main { flex: 1 1 220px; min-width: 0; display: grid; gap: 2px; }
.pk-main b { font-weight: 600; color: #16322A; overflow-wrap: anywhere; }
.pk-main small { font-size: 13px; color: #3E504A; }
.pk-tag { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: #EEF3FC; color: #2B3F66; margin-left: 6px; vertical-align: 1px; }
.pk-actions { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; }
.pk-ask { font-size: 14px; color: #3E504A; }
.pk-link { background: none; border: 0; padding: 4px 0; font: inherit; font-size: 14px; font-weight: 600; color: #2E7D32; cursor: pointer; }
.pk-link:hover { color: #1B5E20; text-decoration: underline; }
.pk-link--danger { color: #B3261E; }
.pk-link--danger:hover { color: #8c1d17; }
.pk-link--quiet { color: #3E504A; }
.pk-link:disabled { opacity: .5; cursor: default; }
.pk-empty { margin: 0; color: #5B6B66; }
.pk-add { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
.pk-field { display: grid; gap: 4px; flex: 1 1 200px; max-width: 320px; }
.pk-field label { font-size: 13px; font-weight: 600; color: #16322A; }
.pk-input { height: 40px; border: 1.5px solid #9AAAA4; border-radius: 7px; padding: 0 10px; font: inherit; background: #fff; color: #16322A; width: 100%; }
.pk-input:focus { outline: none; border-color: #1F5FD1; box-shadow: 0 0 0 3px #EEF3FC; }
.pk-btn { height: 40px; padding: 0 18px; border: 0; border-radius: 6px; background: #2E7D32; color: #fff; font: inherit; font-weight: 700; font-size: 14.5px; cursor: pointer; }
.pk-btn:hover { background: #1B5E20; }
.pk-btn:disabled { opacity: .6; cursor: default; }
.pk-btn--outline { background: #fff; color: #1B5E20; border: 1.5px solid #2E7D32; }
.pk-btn--outline:hover { background: #EAF3EA; }
.pk-tip { margin: 0; font-size: 13.5px; color: #3E504A; max-width: 68ch; }
.pk :focus-visible { outline: 3px solid #1F5FD1; outline-offset: 2px; }
.pk-note { margin: 0; padding: 10px 12px; border-radius: 8px; font-size: 14px; }
.pk-note--warn { background: #FFF4E0; color: #5B3700; }
.pk-note--ok { background: #EAF3EA; color: #1B5E20; }
.pk-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
</style>
